import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import API from '../api/axiosConfig';
import MathText from '../components/MathText';
import TopicSelect from '../components/TopicSelect';
import { useAuth } from '../context/AuthContext';

function TestConstructor() {
  const navigate = useNavigate();
  const { userData } = useAuth();

  // Основные стейты данных
  const [subjects, setSubjects] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [loadingQuestions, setLoadingQuestions] = useState(false);
  
  // Данные создаваемого шаблона
  const [testTitle, setTestTitle] = useState('');
  const [selectedQuestions, setSelectedQuestions] = useState([]); // массив ID выбранных вопросов

  // Стейты фильтров (Левая панель)
  const [filterSubject, setFilterSubject] = useState('');
  const [filterClass, setFilterClass] = useState('');
  const [filterDifficulty, setFilterDifficulty] = useState('');
  const [filterTopic, setFilterTopic] = useState('');

  const answerLabels = ['А', 'Б', 'В', 'Г', 'Д'];

  // 1. Загружаем предметы при инициализации страницы
  useEffect(() => {
    API.get('/api/subjects/')
      .then(res => setSubjects(res.data))
      .catch(err => console.error("Ошибка загрузки предметов:", err));
  }, []);

  // 2. Функция загрузки и фильтрации вопросов из банка
  const fetchFilteredQuestions = useCallback(async () => {
    setLoadingQuestions(true);
    try {
      // Запрашиваем все вопросы. Фильтрацию по теме, классу и сложности сделаем на клиенте для быстродействия,
      // либо можно передавать query-параметры, если бэкенд их поддерживает.
      const response = await API.get('/api/questions/');
      let data = response.data;

      // Применяем фильтры
      if (filterSubject) data = data.filter(q => q.subject === parseInt(filterSubject));
      if (filterClass) data = data.filter(q => q.class_level === parseInt(filterClass));
      if (filterDifficulty) data = data.filter(q => q.difficulty === parseInt(filterDifficulty));
      if (filterTopic) data = data.filter(q => q.topic === parseInt(filterTopic));

      setQuestions(data);
    } catch (error) {
      console.error("Ошибка загрузки вопросов:", error);
    } finally {
      setLoadingQuestions(false);
    }
  }, [filterSubject, filterClass, filterDifficulty, filterTopic]);

  // Триггерим поиск вопросов при изменении любого фильтра
  useEffect(() => {
    fetchFilteredQuestions();
  }, [fetchFilteredQuestions]);

  // 3. Обработка клика по чекбоксу вопроса
  const handleToggleQuestion = (id) => {
    if (selectedQuestions.includes(id)) {
      setSelectedQuestions(selectedQuestions.filter(qId => qId !== id));
    } else {
      setSelectedQuestions([...selectedQuestions, id]);
    }
  };

  // 4. Сохранение шаблона теста на бэкенд
  const handleSaveTemplate = async (e) => {
    e.preventDefault();
    if (!testTitle.trim()) {
      alert("⚠️ Пожалуйста, введите название теста.");
      return;
    }
    if (selectedQuestions.length === 0) {
      alert("⚠️ Выберите хотя бы один вопрос для создания теста.");
      return;
    }

    try {
      await API.post('/api/test-templates/', {
        title: testTitle,
        questions: selectedQuestions // Бэкенд ждет массив ID благодаря ManyToMany полю
      });
      alert("✅ Шаблон теста успешно сохранен в вашу библиотеку!");
      navigate(-1); // Возвращаемся на предыдущую страницу (например, к управлению группами)
    } catch (error) {
      console.error(error);
      alert("❌ Не удалось сохранить шаблон. Проверьте заполненность полей.");
    }
  };

  return (
    <div className="min-h-screen bg-gray-100 p-4 md:p-6 font-sans">
      <div className="max-w-[1600px] mx-auto space-y-5">
        
        {/* Верхняя шапка */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center bg-white p-5 rounded-2xl border border-gray-200 shadow-sm gap-4">
          <div>
            <h1 className="text-xl font-black text-gray-800">🏗️ Конструктор шаблонов тестов</h1>
            <p className="text-xs text-gray-500 font-medium mt-0.5">Соберите тест из вопросов вашего банка для последующего назначения группам.</p>
          </div>
          <button onClick={() => navigate(-1)} className="bg-gray-100 hover:bg-gray-200 text-xs font-bold px-4 py-2 rounded-xl transition-colors">
            ← Назад
          </button>
        </div>

        {/* Форма названия и кнопка сохранения */}
        <div className="bg-white p-5 rounded-2xl border border-gray-200 shadow-sm">
          <div className="flex flex-col md:flex-row gap-4 items-end">
            <div className="flex-1 w-full">
              <label className="block text-xs font-black uppercase tracking-widest text-gray-400 mb-2">Название шаблона теста</label>
              <input 
                type="text" 
                placeholder="Например: Итоговый контроль по Дробям (Вариант 1)"
                className="w-full px-4 py-2.5 rounded-xl border border-gray-200 text-sm font-medium outline-none focus:border-blue-500 bg-gray-50/50"
                value={testTitle}
                onChange={(e) => setTestTitle(e.target.value)}
              />
            </div>
            <div className="shrink-0 w-full md:w-auto">
              <button 
                onClick={handleSaveTemplate}
                className="w-full md:w-auto bg-blue-600 hover:bg-blue-700 text-white text-xs font-black uppercase tracking-widest px-6 py-3.5 rounded-xl transition-all shadow-sm flex items-center justify-center gap-2"
              >
                💾 Сохранить шаблон ({selectedQuestions.length} вопр.)
              </button>
            </div>
          </div>
        </div>

        {/* Основной двухколоночный контент */}
        <div className="grid grid-cols-1 xl:grid-cols-12 gap-5 items-start">
          
          {/* ЛЕВАЯ ПАНЕЛЬ: Фильтры */}
          <div className="xl:col-span-3 space-y-4 bg-white p-5 rounded-2xl border border-gray-200 shadow-sm xl:sticky xl:top-4">
            <h3 className="text-sm font-black text-gray-800 border-b border-gray-100 pb-2 flex items-center gap-2">
              <span>🔍</span> Фильтрация банка вопросов
            </h3>
            
            {/* Предмет */}
            <div className="space-y-1">
              <label className="text-[10px] font-black uppercase tracking-wider text-gray-400">Предмет</label>
              <select 
                className="w-full px-3 py-2 text-xs font-bold bg-gray-50 border border-gray-200 rounded-lg outline-none focus:border-blue-500"
                value={filterSubject}
                onChange={(e) => { setFilterSubject(e.target.value); setFilterTopic(''); }} // Сбрасываем тему при смене предмета
              >
                <option value="">Все предметы</option>
                {subjects.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </div>

            {/* Класс */}
            <div className="space-y-1">
              <label className="text-[10px] font-black uppercase tracking-wider text-gray-400">Класс</label>
              <select 
                className="w-full px-3 py-2 text-xs font-bold bg-gray-50 border border-gray-200 rounded-lg outline-none focus:border-blue-500"
                value={filterClass}
                onChange={(e) => setFilterClass(e.target.value)}
              >
                <option value="">Все классы</option>
                {[5, 6, 7, 8, 9, 10, 11].map(lvl => <option key={lvl} value={lvl}>{lvl} класс</option>)}
              </select>
            </div>

            {/* Сложность */}
            <div className="space-y-1">
              <label className="text-[10px] font-black uppercase tracking-wider text-gray-400">Сложность</label>
              <select 
                className="w-full px-3 py-2 text-xs font-bold bg-gray-50 border border-gray-200 rounded-lg outline-none focus:border-blue-500"
                value={filterDifficulty}
                onChange={(e) => setFilterDifficulty(e.target.value)}
              >
                <option value="">Любая сложность</option>
                {[1, 2, 3, 4, 5].map(diff => <option key={diff} value={diff}>Уровень {diff}</option>)}
              </select>
            </div>

            {/* Тема (Кастомный TopicSelect, который мы настроили на прошлом шаге) */}
            <div className="space-y-1">
              <label className="text-[10px] font-black uppercase tracking-wider text-gray-400">Тема предмета</label>
              <TopicSelect 
                selectedSubjectId={filterSubject}
                value={filterTopic}
                onChange={(val) => setFilterTopic(val)}
                currentUser={userData}
                className="w-full px-3 py-2 text-xs font-bold bg-gray-50 border border-gray-200 rounded-lg outline-none"
              />
            </div>

            {/* Кнопка сброса фильтров */}
            <button 
              onClick={() => { setFilterSubject(''); setFilterClass(''); setFilterDifficulty(''); setFilterTopic(''); }}
              className="w-full mt-2 py-2 bg-gray-100 hover:bg-gray-200 text-gray-600 text-xs font-bold rounded-xl transition-colors"
            >
              Сбросить все фильтры
            </button>
          </div>

          {/* ПРАВАЯ ПАНЕЛЬ: Вопросы с чекбоксами */}
          <div className="xl:col-span-9 space-y-4">
            <div className="flex justify-between items-center bg-white p-3 rounded-xl border border-gray-200 shadow-sm text-xs font-bold text-gray-500">
              <span>Найдено вопросов в банке: <span className="text-blue-600 font-black">{questions.length}</span></span>
              <span>Выбрано для теста: <span className="text-green-600 font-black">{selectedQuestions.length}</span></span>
            </div>

            {loadingQuestions ? (
              <div className="flex justify-center p-12 bg-white rounded-2xl border border-gray-200">
                <div className="w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
              </div>
            ) : questions.length === 0 ? (
              <div className="text-center p-12 bg-white rounded-2xl border border-gray-200 text-sm text-gray-400 font-medium">
                📭 Вопросы по выбранным фильтрам не найдены. Измените настройки фильтрации слева.
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {questions.map(q => {
                  const isChecked = selectedQuestions.includes(q.id);
                  const subObj = subjects.find(s => s.id === q.subject);

                  return (
                    <div 
                      key={q.id}
                      onClick={() => handleToggleQuestion(q.id)}
                      className={`p-5 rounded-2xl border shadow-sm flex gap-4 transition-all bg-white cursor-pointer select-none ${
                        isChecked ? 'ring-2 ring-blue-500 border-blue-300 bg-blue-50/20' : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      {/* Чекбокс */}
                      <div className="pt-0.5 shrink-0">
                        <input 
                          type="checkbox"
                          checked={isChecked}
                          onChange={() => {}} // Переключение обрабатывается кликом на всю карточку
                          className="w-4 h-4 rounded text-blue-600 cursor-pointer"
                        />
                      </div>

                      {/* Тело вопроса */}
                      <div className="flex-1 space-y-3 min-w-0">
                        <div className="flex flex-wrap items-center gap-1.5">
                          <span className="px-2 py-0.5 bg-gray-100 border border-gray-200 rounded text-[8px] font-bold text-gray-500 uppercase tracking-widest">
                            {subObj?.name} • {q.class_level} кл.
                          </span>
                          <span className="px-2 py-0.5 bg-yellow-50 border border-yellow-200 text-yellow-600 rounded text-[8px] font-bold uppercase tracking-widest">
                            Ур.{q.difficulty}
                          </span>
                        </div>

                        <div className="text-xs font-bold text-gray-800 leading-relaxed">
                          <MathText text={q.text} />
                        </div>

                        {q.image && (
                          <img src={q.image} alt="Вопрос" className="w-full object-contain max-h-24 rounded-lg bg-gray-50 border" />
                        )}

                        {/* Варианты ответов */}
                        <div className="space-y-1 pl-2 border-l border-gray-200 text-[10px]">
                          {q.answers?.map((ans, idx) => (
                            <div key={idx} className="flex gap-1.5">
                              <span className={`font-black ${ans.is_correct ? 'text-green-600' : 'text-gray-400'}`}>
                                {answerLabels[idx]})
                              </span>
                              <span className={`${ans.is_correct ? 'text-green-700 font-bold' : 'text-gray-600 font-medium'} truncate`}>
                                <MathText text={ans.text} />
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>

                    </div>
                  );
                })}
              </div>
            )}

          </div>

        </div>

      </div>
    </div>
  );
}

export default TestConstructor;